import React from "react";
import {Checkbox, Flex} from "@chakra-ui/react";
import LanguageInput from "./LanguageInput.tsx";
import LanguageConfiguration from "./LanguageConfiguration.tsx";
import type {ILanguageConfiguration} from "../../models/interfaces/languageConfiguration.interface.ts";
import LanguageDefaultConfiguration from "../LanguageDefaultConfigurationIcon.tsx";

interface StepSourceLanguageProps {
  sourceLanguages: string[];
  selectedSourceLang: string;
  config: Partial<ILanguageConfiguration>;
  onSelect: (lang: string) => void;
  onClearSource: () => void;
  onUpdateConfig: (config: Partial<ILanguageConfiguration>) => void;
  transcribeOnly?: boolean;
  toggleIsTranscribeOnly: () => void;
  keepSongs?: boolean;
  toggleKeepSongs: () => void;
  dubbingScript?: boolean;
  toggleDubbingScript: () => void;
}

const StepSourceLanguage: React.FC<StepSourceLanguageProps> = (
  {
    sourceLanguages,
    selectedSourceLang,
    config,
    onSelect,
    onClearSource,
    onUpdateConfig,
    transcribeOnly,
    toggleIsTranscribeOnly,
    keepSongs,
    toggleKeepSongs,
    dubbingScript,
    toggleDubbingScript,
  }: StepSourceLanguageProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(selectedSourceLang);
  const keepSongsHintId: string = React.useId();
  const dubbingScriptHintId: string = React.useId();

  return (
    <Flex className="flex-col gap-2">
      <LanguageInput
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        languages={sourceLanguages}
        selectedLanguage={inputValue}
        onSelectLanguage={(lang: string) => {
          setInputValue(lang);
          onSelect(lang);
        }}
        onChange={(value: string) => {
          setInputValue(value);
          if (selectedSourceLang !== "" && value !== selectedSourceLang) onClearSource();
        }}
        placeholder="Select source language"
      />
      <LanguageConfiguration
        config={config}
        setConfig={onUpdateConfig}
        isDisabled={selectedSourceLang === ""}
        action={
          <LanguageDefaultConfiguration
            lang={selectedSourceLang}
            currentConfig={config}
            updateConfig={onUpdateConfig}
          />
        }
      />

      <Checkbox
        mt={2}
        colorScheme="brandPurple"
        color="white"
        isChecked={Boolean(transcribeOnly)}
        onChange={() => toggleIsTranscribeOnly()}
      >
        Transcribe only
      </Checkbox>

      <div className="flex flex-col items-start">
        <Checkbox
          colorScheme="brandPurple"
          color="white"
          data-testid="keep-songs-checkbox"
          aria-describedby={keepSongsHintId}
          isChecked={Boolean(keepSongs)}
          onChange={() => toggleKeepSongs()}
        >
          Keep songs
        </Checkbox>
        <p id={keepSongsHintId} className="mt-1 pl-6 text-xs text-n-300 text-pretty">
          Keep sung lyrics in the subtitles
        </p>
      </div>

      <div className="flex flex-col items-start">
        <Checkbox
          colorScheme="brandPurple"
          color="white"
          data-testid="dubbing-script-checkbox"
          aria-describedby={dubbingScriptHintId}
          isChecked={Boolean(dubbingScript)}
          onChange={() => toggleDubbingScript()}
        >
          Dubbing script
        </Checkbox>
        <p id={dubbingScriptHintId} className="mt-1 pl-6 text-xs text-n-300 text-pretty">
          Also export a dubbing script with a speaker persona per line
        </p>
      </div>
    </Flex>
  );
};

export default StepSourceLanguage;
